import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Settings, Check, ChevronRight, Heart, Droplets, Sunrise, Utensils, Zap, Users } from 'lucide-react';
import { WaterTracker } from '@/components/WaterTracker';
import { CoachPanel } from '@/components/CoachPanel';
import JustTellMeModal from '@/components/JustTellMeModal';
import axios from 'axios';
import { C, GREETINGS, API } from './dashboardConstants';
import { getTimeOfDay } from '@/config/motivationalImages';
import { MealRow } from './MealRow';
import { format } from 'date-fns';
import logger from '@/utils/logger';
import { HealthToolsSection } from './HealthToolsSection';
import { HealthToolDiscovery } from './HealthToolDiscovery';
import { NuriCompanion } from '@/components/NuriCompanion';

export default function GentleDashboard() {
  const navigate = useNavigate();
  const { user, authHeaders, token } = useAuth();
  const [dashData, setDashData] = useState(null);
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCoach, setShowCoach] = useState(false);
  const [completingOneThing, setCompletingOneThing] = useState(false);
  const [showJustTellMe, setShowJustTellMe] = useState(false);
  const [monthReview, setMonthReview] = useState(null);

  const timeOfDay = getTimeOfDay();
  const firstName = user?.name?.split(' ')[0] || 'there';
  const dateStr = format(new Date(), 'yyyy-MM-dd');

  useEffect(() => {
    if (!user) return;
    fetchAll();
  }, [user]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [dashRes, mealsRes, reviewRes] = await Promise.all([
        axios.get(`${API}/gentle/dashboard`, authHeaders()),
        axios.get(`${API}/meals?date=${dateStr}`, authHeaders()),
        axios.get(`${API}/gentle/month-in-review`, authHeaders()).catch(() => ({ data: null })),
      ]);
      setDashData(dashRes.data);
      setMeals(mealsRes.data);
      if (reviewRes.data?.has_data) setMonthReview(reviewRes.data);
    } catch (err) {
      logger.error('Gentle dashboard fetch failed:', err);
    }
    setLoading(false);
  };

  const handleCompleteOneThing = async () => {
    setCompletingOneThing(true);
    try {
      await axios.post(`${API}/gentle/one-thing/complete`, {}, authHeaders());
      await fetchAll();
    } catch (err) {
      logger.error('Complete one thing failed:', err);
    }
    setCompletingOneThing(false);
  };

  const handleRecordWin = async (action) => {
    try {
      await axios.post(`${API}/gentle/tiny-win?action=${action}`, {}, authHeaders());
      await fetchAll();
    } catch (err) {
      logger.error('Record win failed:', err);
    }
  };

  if (loading || !dashData) {
    return (
      <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: C.muted, fontFamily: 'var(--font-sans)' }}>Loading...</p>
      </div>
    );
  }

  const { tiny_wins, insight, one_thing, meals_this_week } = dashData;
  const trendData = tiny_wins?.weekly_trend || [];
  const maxTrend = Math.max(...trendData.map(t => t.points), 1);

  return (
    <div style={{ minHeight: '100vh', background: C.bg, paddingBottom: 100 }} data-testid="gentle-dashboard">

      {/* Header */}
      <header style={{ background: C.bg, position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 600, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 56 }}>
          <img src="/nuri-logo-mark.svg" alt="nuri" style={{ height: 32, cursor: 'pointer' }} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} />
          <button data-testid="gentle-settings-btn" onClick={() => navigate('/settings')} style={{ padding: 8, background: 'none', border: 'none', cursor: 'pointer' }}>
            <Settings className="w-5 h-5" style={{ color: C.muted }} />
          </button>
        </div>
      </header>

      <main style={{ maxWidth: 600, margin: '0 auto', padding: '16px 24px 120px' }}>

        {/* Greeting + Nuri Companion */}
        <div className="nuri-reveal nuri-d0" style={{ marginBottom: insight ? 0 : 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <NuriCompanion state={new Date().getHours() >= 23 || new Date().getHours() < 5 ? 'sleeping' : meals.length >= 2 ? 'happy' : 'empathetic'} size="sm" />
            <p data-testid="gentle-greeting" style={{ fontFamily: 'var(--font-serif)', fontSize: 36, margin: 0, color: C.text, fontWeight: 400 }}>
              {GREETINGS[timeOfDay]}, {firstName}
            </p>
          </div>
          <p style={{ fontSize: 14, margin: '8px 0 0', color: C.muted, fontFamily: 'var(--font-sans)' }}>
            You showed up today. That counts.
          </p>
        </div>

        {/* AI Insight — flows naturally from greeting */}
        {insight && (
          <div className="nuri-reveal nuri-d0" data-testid="gentle-insight-card" style={{
            padding: '10px 0 0', marginBottom: 28,
          }}>
            <p data-testid="gentle-insight-text" style={{ fontSize: 15, margin: 0, color: C.text, lineHeight: 1.6, fontFamily: 'var(--font-sans)' }}>
              {insight}
            </p>
          </div>
        )}

        {/* Just Tell Me What to Eat */}
        <div className="nuri-reveal nuri-d1" style={{ marginBottom: 24 }}>
          <button
            data-testid="just-tell-me-btn"
            onClick={() => setShowJustTellMe(true)}
            style={{
              width: '100%', padding: '20px 22px', borderRadius: 20,
              background: 'linear-gradient(135deg, #5A8C6A 0%, #4A7C5A 100%)', color: '#FDFBF7',
              border: 'none', fontFamily: 'var(--font-sans)', fontSize: 16, fontWeight: 600,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
              transition: 'transform 0.15s', boxShadow: '0 4px 16px rgba(90,140,106,0.25)',
            }}
          >
            <Zap style={{ width: 20, height: 20 }} />
            Just Tell Me What to Eat
          </button>
        </div>

        {/* Just One Thing */}
        {one_thing && (
          <div className="nuri-reveal nuri-d2" data-testid="gentle-one-thing-card" style={{
            background: C.card, borderRadius: 20, padding: '20px 22px', marginBottom: 24,
            border: `1px solid ${C.border}`,
          }}>
            <p style={{ fontSize: 11, letterSpacing: '0.5px', color: C.gold, fontFamily: 'var(--font-sans)', textTransform: 'uppercase', margin: '0 0 10px', fontWeight: 600 }}>
              <Sunrise style={{ width: 14, height: 14, display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
              Just one thing today
            </p>
            <p data-testid="gentle-one-thing-text" style={{ fontSize: 16, margin: '0 0 16px', color: C.text, lineHeight: 1.5, fontFamily: 'var(--font-sans)', fontWeight: 500 }}>
              {one_thing.text}
            </p>
            {one_thing.completed ? (
              <div data-testid="gentle-one-thing-done" style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.green }}>
                <Check style={{ width: 18, height: 18 }} />
                <span style={{ fontSize: 14, fontFamily: 'var(--font-sans)', fontWeight: 500 }}>Done! +2 Tiny Wins</span>
              </div>
            ) : (
              <button
                data-testid="gentle-one-thing-complete-btn"
                onClick={handleCompleteOneThing}
                disabled={completingOneThing}
                style={{
                  padding: '12px 24px', borderRadius: 28, background: C.green, color: '#fff',
                  border: 'none', fontFamily: 'var(--font-sans)', fontSize: 14, fontWeight: 500,
                  cursor: 'pointer', opacity: completingOneThing ? 0.6 : 1, transition: 'opacity 0.2s',
                }}
              >
                {completingOneThing ? 'Saving...' : 'I did it'}
              </button>
            )}
          </div>
        )}

        {/* Tiny Wins Counter */}
        <div className="nuri-reveal nuri-d3" data-testid="gentle-tiny-wins-card" style={{
          background: C.card, borderRadius: 20, padding: '20px 22px', marginBottom: 24,
          border: `1px solid ${C.border}`,
        }}>
          <p style={{ fontSize: 11, letterSpacing: '0.5px', color: C.accent, fontFamily: 'var(--font-sans)', textTransform: 'uppercase', margin: '0 0 14px', fontWeight: 600 }}>Tiny Wins</p>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
            <span data-testid="gentle-tiny-wins-total" style={{ fontSize: 40, fontWeight: 700, color: C.text, fontFamily: 'var(--font-sans)', lineHeight: 1 }}>
              {tiny_wins.total_points}
            </span>
            <span style={{ fontSize: 14, color: C.muted, fontFamily: 'var(--font-sans)' }}>total points</span>
          </div>
          <p style={{ fontSize: 13, color: C.green, margin: '0 0 16px', fontFamily: 'var(--font-sans)', fontWeight: 500 }}>
            {tiny_wins.week_points > 0 ? `+${tiny_wins.week_points} this week` : 'Start earning wins today'}
          </p>

          {/* Mini trend chart */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 48 }}>
            {trendData.map((w, i) => (
              <div key={i} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{
                  height: Math.max(w.points / maxTrend * 40, 4),
                  background: i === trendData.length - 1 ? C.green : C.cream,
                  borderRadius: 4, transition: 'height 0.5s ease',
                }} />
                <p style={{ fontSize: 9, color: C.muted, margin: '4px 0 0', fontFamily: 'var(--font-sans)' }}>{w.week}</p>
              </div>
            ))}
          </div>

          {/* Milestone */}
          {dashData.tiny_wins?.milestone && (
            <p style={{ fontSize: 13, color: C.gold, margin: '12px 0 0', fontFamily: 'var(--font-sans)', fontWeight: 500 }}>
              {dashData.tiny_wins.milestone}
            </p>
          )}
        </div>

        {/* Quick Win Buttons */}
        <div className="nuri-reveal nuri-d3" style={{ marginBottom: 24 }}>
          <p style={{ fontSize: 11, letterSpacing: '0.8px', color: C.muted, fontFamily: 'var(--font-sans)', textTransform: 'uppercase', margin: '0 0 12px', fontWeight: 500 }}>Quick wins</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[
              { action: 'logged_water', label: 'Drank water', icon: Droplets },
              { action: 'logged_veggie', label: 'Ate a veggie', icon: Heart },
            ].map(({ action, label, icon: Icon }) => (
              <button
                key={action}
                data-testid={`gentle-quick-win-${action}`}
                onClick={() => handleRecordWin(action)}
                style={{
                  padding: '10px 18px', borderRadius: 20, background: C.cream,
                  border: 'none', fontFamily: 'var(--font-sans)', fontSize: 13,
                  fontWeight: 500, color: C.textSec, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                  transition: 'background 0.2s',
                }}
              >
                <Icon style={{ width: 14, height: 14 }} />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Meals Logged (no calorie display) */}
        <div className="nuri-reveal nuri-d4" style={{ marginBottom: 24 }}>
          <p style={{ fontSize: 11, letterSpacing: '0.8px', color: C.muted, fontFamily: 'var(--font-sans)', textTransform: 'uppercase', margin: '0 0 12px', fontWeight: 500 }}>
            Today's meals {meals.length > 0 && <span style={{ color: C.green }}>({meals.length} logged)</span>}
          </p>
          {meals.length === 0 ? (
            <div data-testid="gentle-empty-meals" style={{
              textAlign: 'center', padding: '32px 20px', background: C.card,
              borderRadius: 20, border: `1px solid ${C.border}`,
            }}>
              <p style={{ fontSize: 15, margin: '0 0 6px', fontWeight: 500, color: C.text, fontFamily: 'var(--font-sans)' }}>
                No meals logged yet
              </p>
              <p style={{ fontSize: 13, margin: '0 0 16px', color: C.muted, fontFamily: 'var(--font-sans)' }}>
                Whenever you're ready. No pressure.
              </p>
              <button
                data-testid="gentle-log-meal-btn"
                onClick={() => navigate('/log-meal')}
                style={{
                  padding: '12px 28px', borderRadius: 28, background: C.black, color: '#FDFBF7',
                  border: 'none', fontFamily: 'var(--font-sans)', fontSize: 14, fontWeight: 500, cursor: 'pointer',
                }}
              >
                Log a Meal
              </button>
            </div>
          ) : (
            meals.map(meal => (
              <MealRow key={meal.meal_id} meal={meal} token={token} onClick={() => navigate(`/meal/${meal.meal_id}`)} />
            ))
          )}
        </div>

        {/* Water */}
        <div className="nuri-reveal nuri-d5" style={{ marginBottom: 24 }}>
          <WaterTracker />
        </div>

        {/* Health Tool Discovery Prompt (one-time) */}
        <HealthToolDiscovery />

        {/* Health Tools (Gentle: only enabled tools shown) */}
        <HealthToolsSection />

        {/* Talk to Coach */}
        <div className="nuri-reveal nuri-d5" style={{ marginBottom: 12 }}>
          <button
            data-testid="gentle-coach-btn"
            onClick={() => setShowCoach(true)}
            style={{
              width: '100%', padding: '18px 22px', borderRadius: 20,
              background: C.cream, border: `1px solid ${C.border}`,
              fontFamily: 'var(--font-sans)', fontSize: 15, fontWeight: 500,
              color: C.text, cursor: 'pointer', textAlign: 'left',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
          >
            <span>Talk to Nuri Coach</span>
            <ChevronRight style={{ width: 18, height: 18, color: C.muted }} />
          </button>
        </div>

        {/* Fast Food Intelligence */}
        <div className="nuri-reveal nuri-d5" style={{ marginBottom: 24 }}>
          <button
            data-testid="gentle-fast-food-btn"
            onClick={() => navigate('/fast-food')}
            style={{
              width: '100%', padding: '18px 22px', borderRadius: 20,
              background: C.cream, border: `1px solid ${C.border}`,
              fontFamily: 'var(--font-sans)', fontSize: 15, fontWeight: 500,
              color: C.text, cursor: 'pointer', textAlign: 'left',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Utensils style={{ width: 16, height: 16, color: C.accent }} />
              <span>Smart Swaps at Fast Food</span>
            </div>
            <ChevronRight style={{ width: 18, height: 18, color: C.muted }} />
          </button>
        </div>

        {/* Month in Review — positive only */}
        {monthReview && monthReview.stats.length > 0 && (
          <div className="nuri-reveal nuri-d4" data-testid="month-in-review" style={{
            padding: '20px', borderRadius: 20, background: C.card,
            border: `1px solid ${C.border}`, marginBottom: 16,
          }}>
            <p style={{
              fontSize: 11, letterSpacing: '0.8px', textTransform: 'uppercase',
              color: C.muted, fontFamily: 'var(--font-sans)', fontWeight: 500, margin: '0 0 12px',
            }}>
              {monthReview.month} in review
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {monthReview.stats.map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 22, fontWeight: 700, color: C.text, fontFamily: 'var(--font-sans)' }}>
                    {s.value}
                  </span>
                  <span style={{ fontSize: 14, color: C.textSec, fontFamily: 'var(--font-sans)' }}>
                    {s.label}{s.unit ? ` ${s.unit}` : ''}
                  </span>
                  {s.improvement && (
                    <span style={{
                      fontSize: 11, color: C.green, fontWeight: 500,
                      fontFamily: 'var(--font-sans)', marginLeft: 'auto',
                    }}>
                      {s.improvement}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Why I Eat / Motivational */}
        <div className="nuri-reveal nuri-d5" style={{ marginBottom: 12 }}>
          <button
            data-testid="gentle-why-i-eat-btn"
            onClick={() => navigate('/why-i-eat')}
            style={{
              width: '100%', padding: '18px 22px', borderRadius: 20,
              background: C.cream, border: `1px solid ${C.border}`,
              fontFamily: 'var(--font-sans)', fontSize: 15, fontWeight: 500,
              color: C.text, cursor: 'pointer', textAlign: 'left',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Heart style={{ width: 16, height: 16, color: C.green }} />
              <span>Why I Eat</span>
            </div>
            <ChevronRight style={{ width: 18, height: 18, color: C.muted }} />
          </button>
        </div>

        {/* Family Mode */}
        <div className="nuri-reveal nuri-d5" style={{ marginBottom: 24 }}>
          <button
            data-testid="gentle-family-mode-btn"
            onClick={() => navigate('/family-mode')}
            style={{
              width: '100%', padding: '18px 22px', borderRadius: 20,
              background: C.cream, border: `1px solid ${C.border}`,
              fontFamily: 'var(--font-sans)', fontSize: 15, fontWeight: 500,
              color: C.text, cursor: 'pointer', textAlign: 'left',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Users style={{ width: 16, height: 16, color: C.accent }} />
              <span>Family Mode</span>
            </div>
            <ChevronRight style={{ width: 18, height: 18, color: C.muted }} />
          </button>
        </div>

        <div style={{ height: 32 }} />
      </main>

      {/* Coach Panel */}
      <CoachPanel isOpen={showCoach} onClose={() => setShowCoach(false)} />

      {/* Just Tell Me Modal */}
      <JustTellMeModal isOpen={showJustTellMe} onClose={() => setShowJustTellMe(false)} />
    </div>
  );
}
